import {
    Box,
    VStack,
    Text,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Flex,
    Badge,
} from '@chakra-ui/react';
import { PanelLeft } from 'lucide-react';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore, useLayers, useEntities, type Entity } from '../../state';

/**
 * Object list row (memoized): a selection click used to rebuild every row's
 * DOM because the inline Flex had a fresh onClick closure per render and no
 * memo boundary. Rows now only re-render when their entity data or their own
 * selected flag changes.
 */
const EntityRow = memo(function EntityRow({ entity, selected, onSelect }: {
    entity: Entity;
    selected: boolean;
    onSelect: (id: string, additive: boolean) => void;
}) {
    return (
        <Flex
            px={3}
            py={1.5}
            align="center"
            gap={2}
            cursor="pointer"
            bg={selected ? 'whiteAlpha.100' : 'transparent'}
            _hover={{ bg: 'whiteAlpha.50' }}
            onClick={(e) => onSelect(entity.id, e.ctrlKey || e.shiftKey)}
        >
            <Text flex="1" fontSize="sm" color="gray.200" noOfLines={1}>
                {entity.name}
            </Text>
            <Text fontSize="xs" color="gray.500">
                {entity.type}
            </Text>
        </Flex>
    );
});

/**
 * Left Sidebar Component — the body of the 2D/3D workbenches' "Project
 * Browser" side panel. Rendered inside the shared panel shell
 * (@suite/panels), which owns the panel chrome: dock edge, width, resize,
 * collapse, float and the tab header (registered as the 'sidebar' panel in
 * src/modules/cad/panels/registerCadPanels.tsx). This component renders
 * content only.
 *
 * Contains:
 * - Layers (minimal status list — the full editing surface lives in the
 *   Layer Properties Manager palette, Faz 5.4/5.12 single-source rule)
 * - Object List
 */
export function Sidebar() {
    const { t } = useTranslation();
    const layers = useLayers();
    const entities = useEntities();
    const selectedIds = useAppStore((s) => s.selectedIds);
    const select = useAppStore((s) => s.select);
    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const setActiveLayer = useAppStore((s) => s.setActiveLayer);
    const toggleLayerManager = useAppStore((s) => s.toggleLayerManager);

    // Memoized list derivations: the sort ran in the render body before, so
    // every unrelated store write re-sorted and rebuilt the arrays.
    const layerList = useMemo(() => Object.values(layers).sort((a, b) => a.order - b.order), [layers]);
    const entityList = useMemo(() => Object.values(entities), [entities]);
    const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

    // Stable row handler: keeps EntityRow's memo boundary effective. Reads
    // selectedIds through getState() so the callback never goes stale and the
    // additive-append behavior matches the original inline handler exactly.
    const handleEntitySelect = useCallback((id: string, additive: boolean) => {
        const ids = useAppStore.getState().selectedIds;
        select(additive ? [...ids, id] : [id]);
    }, [select]);

    return (
        <Box data-tour-id="task-panels">
            {/* Accordion Sections */}
            <Accordion defaultIndex={[0, 1]} allowMultiple>
                    {/* Layers Section — minimal status list: active highlight,
                        color swatch, name; visibility/freeze/lock/plot/style
                        editing moved to the Layer Properties Manager palette
                        (Faz 5.12: no double maintenance). */}
                    <AccordionItem border="none">
                        <AccordionButton px={3} py={2} _hover={{ bg: 'whiteAlpha.50' }}>
                            <Flex flex="1" align="center" gap={2}>
                                <Text fontSize="xs" fontWeight="medium" color="gray.400">{t('ui.sidebar.layers')}</Text>
                                <Badge size="sm" colorScheme="blue">{layerList.length}</Badge>
                            </Flex>
                            <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel p={0}>
                            <VStack spacing={0} align="stretch">
                                {layerList.map((layer) => (
                                    <Flex
                                        key={layer.id}
                                        px={3}
                                        py={1.5}
                                        align="center"
                                        gap={2}
                                        cursor="pointer"
                                        bg={activeLayerId === layer.id ? 'whiteAlpha.100' : 'transparent'}
                                        _hover={{ bg: 'whiteAlpha.50' }}
                                        onClick={() => setActiveLayer(layer.id)}
                                    >
                                        {/* Layer Color Swatch */}
                                        <Box
                                            w={3}
                                            h={3}
                                            borderRadius="sm"
                                            bg={layer.color}
                                            border="1px solid"
                                            borderColor="whiteAlpha.300"
                                            flexShrink={0}
                                        />

                                        {/* Layer Name (active marker rides the row bg) */}
                                        <Text flex="1" fontSize="sm" color="gray.200" noOfLines={1}>
                                            {layer.name}
                                        </Text>

                                        {/* Minimal status dot: dimmed when the layer
                                            is off/frozen (display state only). */}
                                        {(!layer.visible || layer.frozen) && (
                                            <Box w="6px" h="6px" borderRadius="full" bg="gray.500" flexShrink={0} />
                                        )}
                                    </Flex>
                                ))}

                                {/* Open the Layer Properties Manager (single
                                    editing surface for the layer table). */}
                                <Flex
                                    px={3}
                                    py={1.5}
                                    align="center"
                                    gap={2}
                                    cursor="pointer"
                                    color="gray.500"
                                    _hover={{ bg: 'whiteAlpha.50', color: 'gray.300' }}
                                    onClick={toggleLayerManager}
                                >
                                    <PanelLeft size={13} />
                                    <Text fontSize="sm">{t('ui.sidebar.openLayerManager')}</Text>
                                </Flex>
                            </VStack>
                        </AccordionPanel>
                    </AccordionItem>

                    {/* Objects Section */}
                    <AccordionItem border="none">
                        <AccordionButton px={3} py={2} _hover={{ bg: 'whiteAlpha.50' }}>
                            <Flex flex="1" align="center" gap={2}>
                                <Text fontSize="xs" fontWeight="medium" color="gray.400">{t('ui.sidebar.objects')}</Text>
                                <Badge size="sm" colorScheme="green">{entityList.length}</Badge>
                            </Flex>
                            <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel p={0}>
                            <VStack spacing={0} align="stretch">
                                {entityList.length === 0 ? (
                                    <Box px={3} py={2}>
                                        <Text fontSize="sm" color="gray.500">{t('ui.sidebar.noObjectsInScene')}</Text>
                                    </Box>
                                ) : (
                                    entityList.map((entity) => (
                                        <EntityRow
                                            key={entity.id}
                                            entity={entity}
                                            selected={selectedIdSet.has(entity.id)}
                                            onSelect={handleEntitySelect}
                                        />
                                    ))
                                )}
                            </VStack>
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
        </Box>
    );
}
